import * as React from "react";
import { useState, useEffect } from "react";
import { Variable, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface CreateVariableModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateVariable: (name: string, value: string) => Promise<void>;
}

const CreateVariableModal: React.FC<CreateVariableModalProps> = ({
  isOpen,
  onClose,
  onCreateVariable
}) => {
  const [variableName, setVariableName] = useState('');
  const [variableValue, setVariableValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setVariableName('');
    setVariableValue('');
    setError(null);
  };

  const handleCreate = async () => {
    if (!variableName.trim()) {
      setError('Enter a variable name');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await onCreateVariable(variableName.trim(), variableValue.trim());

      resetForm();
      onClose();
    } catch (err) {
      console.error('[CreateVariableModal] Error creating variable:', err);
      setError(err instanceof Error ? err.message : 'Unknown error creating the variable');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleCreate();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[450px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Variable className="h-5 w-5 text-blue-600" />
            <DialogTitle>New Variable</DialogTitle>
          </div>
          <DialogDescription>
            Create a new variable for your project. Variables allow you to store key data associated with your execution.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="variable-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="variable-name"
              placeholder="variable_name"
              value={variableName}
              onChange={(e) => setVariableName(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Use a descriptive name (e.g., api_key, project_name, start_date)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="variable-value">Value</Label>
            <Input
              id="variable-value"
              placeholder="Variable value"
              value={variableValue}
              onChange={(e) => setVariableValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              You can leave it empty and assign it later
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={loading || !variableName.trim()}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Variable'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateVariableModal;

